<script setup lang="ts">
import { useAxios } from '@vueuse/integrations/useAxios';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { TIMECLOCK_ENDPOINTS } from '../api/time-clock.service';
import Notifications from '../components/notifications/Notifications.vue';
import { api } from '../helpers/axiosHelper';
import { Notification as NotificationInterface, NotificationType } from '../interfaces/notification.interface';
import { TimeClock } from '../interfaces/time-clock.interface';

const route = useRoute();

const sync_id = ref<string>(route?.params?.sync_id.toString());
const { data: timeClock } = useAxios<TimeClock>(TIMECLOCK_ENDPOINTS.GET(sync_id.value), api);

// #region check version number
const interval_camera_stream_check = ref<NodeJS.Timeout>();
onMounted(() => {
   const FIVE_MINUTES = 5 * 60 * 1000;
   interval_camera_stream_check.value = setInterval(getLatestApiKey, FIVE_MINUTES);
});

function getLatestApiKey() {
   api.get(TIMECLOCK_ENDPOINTS.GET(sync_id.value));
}

onBeforeUnmount(() => {
   if (interval_camera_stream_check.value) clearInterval(interval_camera_stream_check.value);
});
// #endregion

// #region notification
const notifications = ref<NotificationInterface[]>([]);

function setNotification(notification: NotificationInterface) {
   removeNotification(notification.id ?? '');
   if (notification.is_infinite) return;

   notifications.value.push(notification);

   setTimeout(() => {
      removeNotification(notification.id ?? '');
   }, 5000);
}

function removeNotification(notification_id: string) {
   const INDEX_OF_NOTIFICATION_TO_REMOVE = notifications.value.findIndex((n) => n.id === notification_id);
   if (INDEX_OF_NOTIFICATION_TO_REMOVE < 0) return;
   notifications.value.splice(INDEX_OF_NOTIFICATION_TO_REMOVE, 1);
}
// #endregion

// #region offline modus
const interval_check_internet_connection = ref<any>(null);
onMounted(() => {
   const ONE_MINUTE = 60 * 1000;
   interval_check_internet_connection.value = setInterval(checkIfInternetIsAvailable, ONE_MINUTE);
});

onBeforeUnmount(() => {
   if (interval_check_internet_connection.value) clearInterval(interval_check_internet_connection.value);
});

const is_offline = ref<boolean>(false);
function checkIfInternetIsAvailable() {
   is_offline.value = !window.navigator.onLine;

   if (is_offline.value) {
      notifications.value.push({
         id: 'offline',
         type: NotificationType.Error,
         is_infinite: true,
         message: 'Offline',
      });
   } else removeNotification('offline');
}
// #endregion

// #region date
const date = ref<string>('');

const interval_update_time = ref<NodeJS.Timeout>();
onMounted(() => {
   updateTime();
   interval_update_time.value = setInterval(updateTime, 1000);
});

onBeforeUnmount(() => {
   if (interval_update_time.value) clearInterval(interval_update_time.value);
});

function updateTime() {
   const now = new Date();
   const hours = now.getHours();
   const minutes = now.getMinutes();
   const seconds = now.getSeconds();

   // Format the string with leading zeroes
   const DATE_TIME = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
   date.value = DATE_TIME;
}
// #endregion
</script>

<template>
   <div class="time-clock__background"></div>
   <div v-if="timeClock" class="time-clock">
      <div class="time-clock__header">
         <div class="time-clock__header__name">{{ timeClock?.name }}</div>
         <div class="time-clock__header__date">{{ date }}</div>
         <div class="time-clock__header__return"></div>
      </div>
      <div class="time-clock__content">
         <RouterView v-slot="{ Component }">
            <Component
               :is="Component"
               :is-offline="is_offline"
               :time-clock="timeClock"
               :sync-id="timeClock.sync_id"
               @set-notification="setNotification"
            />
         </RouterView>
      </div>
      <div class="time-clock__footer">
         <img src="/nxt-people-logo.svg" />
      </div>
   </div>
   <Notifications :notifications="notifications" />
</template>

<style lang="scss" scoped>
.time-clock__background {
   position: fixed;
   left: 0;
   top: 0;

   width: 100%;
   height: 100%;
   overflow: hidden;

   background-image: url('/time-clock-background.png');
   background-repeat: no-repeat;
   background-size: cover;

   &::after {
      content: '';

      position: fixed;
      z-index: 1;

      left: 0;
      top: 0;

      width: 100%;
      height: 100%;

      background-color: rgba($color: #081c4a, $alpha: 0.9);
   }
}

.time-clock {
   display: flex;
   justify-content: center;
   align-items: center;
   flex-direction: column;

   height: 100%;
   width: 100%;

   overflow: hidden;
}

.time-clock__content {
   display: flex;
   align-items: center;
   width: 100%;
   height: 80%;
   padding: var(--nxt-gutter-large) 0;
}

.time-clock__header,
.time-clock__footer {
   z-index: 2;

   display: flex;
   align-items: center;

   width: calc(100% - var(--nxt-gutter-large) * 4);
   height: 10%;
   padding: 0 calc(var(--nxt-gutter-large) * 2);
}

.time-clock__header {
   // display: flex;
   display: grid;
   grid-template-columns: 1fr 1fr 1fr;
   gap: var(--nxt-gutter);
   color: var(--nxt-white);

   &__name {
      font-size: calc(var(--nxt-font-large) * 1.5);
      font-weight: var(--nxt-font-bold);
   }

   &__date {
      text-align: center;
      font-size: calc(var(--nxt-font-large) * 1.2);
   }
}

.time-clock__footer {
   justify-content: center;
}

.time-clock__footer img {
   height: 40px;
}
</style>
